/**
 * CountyHero Component
 * Hero section for county pages with key stats and primary CTA
 */

import { Link } from 'react-router-dom';
import type { County } from '../../types/county';

interface CountyHeroProps {
  county: County;
}

export function CountyHero({ county }: CountyHeroProps) {
  return (
    <div className="bg-gradient-to-br from-primary-50 to-primary-100 border-b border-primary-200">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="mb-6">
          <Link to="/counties" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            ← All Counties
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main County Info */}
          <div className="lg:col-span-2">
            <h1 className="text-4xl font-bold text-neutral-900 mb-3">
              {county.name} County
            </h1>
            <p className="text-lg text-neutral-700 mb-6">
              {county.region} Region • Population: {county.population.toLocaleString()}
            </p>

            <div className="flex flex-wrap gap-3 mb-6">
              <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-neutral-200">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
                  Current Diversion Rate
                </div>
                <div className="text-2xl font-bold text-primary-600">
                  {county.diversionRate}%
                </div>
              </div>

              <div className="bg-white rounded-lg px-4 py-3 shadow-sm border border-neutral-200">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">
                  Approved Facilities
                </div>
                <div className="text-2xl font-bold text-primary-600">
                  {county.facilityCount}
                </div>
              </div>

              {county.greenHaloRequired && (
                <div className="bg-green-50 rounded-lg px-4 py-3 shadow-sm border border-green-200">
                  <div className="text-xs uppercase tracking-wide text-green-700 mb-1">
                    Green Halo
                  </div>
                  <div className="text-sm font-semibold text-green-900">
                    Required
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/plans/create"
              className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-sm"
              aria-label="Create waste management plan"
            >
              Create Plan for {county.name} County
            </Link>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-neutral-200">
              <h3 className="font-semibold text-neutral-900 mb-4">Quick Links</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a
                    href="#requirements"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <span>→</span>
                    <span>Local Requirements</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#facilities"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <span>→</span>
                    <span>Facility Directory</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#calculator"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <span>→</span>
                    <span>Diversion Calculator</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#providers"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <span>→</span>
                    <span>Service Providers</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
                  >
                    <span>→</span>
                    <span>County FAQ</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
